/**
 * TradeStack - Settings Page
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import api from '../api';
import './Settings.css';

function Settings() {
  const { user, updateProfile } = useAuth();
  useConfig();

  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: ''
  });

  const [taxSettings, setTaxSettings] = useState({
    filingStatus: 'single',
    state: '',
    otherIncome: 0
  });

  const [brandingData, setBrandingData] = useState({
    logoUrl: '',
    primaryColor: '#2563eb',
    businessAddress: '',
    businessPhone: '',
    businessEmail: '',
    invoiceTerms: 'Payment due within 30 days.',
    estimateTerms: 'Estimate valid for 30 days.'
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        companyName: user.company_name || ''
      });
    }
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    try {
      const response = await api.getUserSettings();
      if (response.data.taxSettings) {
        setTaxSettings(response.data.taxSettings);
      }
      if (response.data.branding) {
        setBrandingData(prev => ({ ...prev, ...response.data.branding }));
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile(profileData);
      showMessage('success', 'Profile updated successfully');
    } catch (err) {
      showMessage('error', 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleTaxSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateUserSettings({ taxSettings });
      showMessage('success', 'Tax settings saved');
    } catch (err) {
      showMessage('error', 'Failed to save tax settings');
    } finally {
      setSaving(false);
    }
  };

  const handleBrandingSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateBranding(brandingData);
      showMessage('success', 'Branding updated');
    } catch (err) {
      showMessage('error', 'Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'profile', name: 'Profile', icon: '👤' },
    { id: 'tax', name: 'Tax Settings', icon: '💰' },
    { id: 'branding', name: 'Branding', icon: '🎨' },
    { id: 'subscription', name: 'Subscription', icon: '💳' }
  ];

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Settings</h1>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="settings-layout">
        {/* Sidebar */}
        <div className="settings-sidebar">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`sidebar-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-name">{tab.name}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="settings-content">
          {/* Profile */}
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileSave} className="settings-form">
              <h2>Profile Settings</h2>
              <p className="form-description">Update your personal information</p>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    value={profileData.firstName}
                    onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={profileData.lastName}
                    onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  value={profileData.companyName}
                  onChange={(e) => setProfileData({ ...profileData, companyName: e.target.value })}
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}

          {/* Tax Settings */}
          {activeTab === 'tax' && (
            <form onSubmit={handleTaxSave} className="settings-form">
              <h2>Tax Settings</h2>
              <p className="form-description">Configure settings for tax calculations</p>

              <div className="form-group">
                <label>Filing Status</label>
                <select
                  value={taxSettings.filingStatus}
                  onChange={(e) => setTaxSettings({ ...taxSettings, filingStatus: e.target.value })}
                >
                  <option value="single">Single</option>
                  <option value="married_filing_jointly">Married Filing Jointly</option>
                  <option value="married_filing_separately">Married Filing Separately</option>
                  <option value="head_of_household">Head of Household</option>
                </select>
              </div>

              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  value={taxSettings.state}
                  onChange={(e) => setTaxSettings({ ...taxSettings, state: e.target.value })}
                  placeholder="e.g., WI"
                  maxLength={2}
                />
              </div>

              <div className="form-group">
                <label>Other Annual Income (W-2, Social Security, etc.)</label>
                <input
                  type="number"
                  value={taxSettings.otherIncome}
                  onChange={(e) => setTaxSettings({ ...taxSettings, otherIncome: parseFloat(e.target.value) || 0 })}
                  step="100"
                />
                <span className="form-hint">
                  Used to calculate your marginal tax rate on business income
                </span>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Tax Settings'}
                </button>
              </div>
            </form>
          )}

          {/* Branding */}
          {activeTab === 'branding' && (
            <form onSubmit={handleBrandingSave} className="settings-form">
              <h2>Branding</h2>
              <p className="form-description">Customize your invoices and estimates</p>

              <div className="form-group">
                <label>Primary Color</label>
                <div className="color-picker">
                  <input
                    type="color"
                    value={brandingData.primaryColor}
                    onChange={(e) => setBrandingData({ ...brandingData, primaryColor: e.target.value })}
                  />
                  <input
                    type="text"
                    value={brandingData.primaryColor}
                    onChange={(e) => setBrandingData({ ...brandingData, primaryColor: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Business Address</label>
                <textarea
                  value={brandingData.businessAddress}
                  onChange={(e) => setBrandingData({ ...brandingData, businessAddress: e.target.value })}
                  rows={3}
                  placeholder="123 Main St&#10;City, State ZIP"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Business Phone</label>
                  <input
                    type="tel"
                    value={brandingData.businessPhone}
                    onChange={(e) => setBrandingData({ ...brandingData, businessPhone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Business Email</label>
                  <input
                    type="email"
                    value={brandingData.businessEmail}
                    onChange={(e) => setBrandingData({ ...brandingData, businessEmail: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Invoice Terms</label>
                <textarea
                  value={brandingData.invoiceTerms}
                  onChange={(e) => setBrandingData({ ...brandingData, invoiceTerms: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Estimate Terms</label>
                <textarea
                  value={brandingData.estimateTerms}
                  onChange={(e) => setBrandingData({ ...brandingData, estimateTerms: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Branding'}
                </button>
              </div>
            </form>
          )}

          {/* Subscription */}
          {activeTab === 'subscription' && (
            <div className="settings-form">
              <h2>Subscription</h2>
              <p className="form-description">Manage your plan and billing</p>

              <div className="current-plan">
                <div className="plan-info">
                  <span className="plan-name">{user?.tier || 'Free'} Plan</span>
                  <span className="plan-price">
                    {user?.tier === 'FREE' ? 'Free' :
                     user?.tier === 'STARTER' ? '$29/month' :
                     user?.tier === 'PRO' ? '$79/month' :
                     user?.tier === 'BUSINESS' ? '$149/month' : 'Contact us'}
                  </span>
                </div>
                <button className="btn btn-primary">Upgrade Plan</button>
              </div>

              <div className="plan-features">
                <h3>Your Plan Includes:</h3>
                <ul>
                  <li>Unlimited customers</li>
                  <li>{user?.tier === 'FREE' ? '3' : 'Unlimited'} active jobs</li>
                  <li>Receipt scanning</li>
                  <li>Mileage tracking</li>
                  <li>Tax reports</li>
                  {user?.tier !== 'FREE' && <li>Invoice customization</li>}
                  {(user?.tier === 'PRO' || user?.tier === 'BUSINESS') && (
                    <>
                      <li>QuickBooks sync</li>
                      <li>Subcontractor management</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;
